import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Grid } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { DateTime } from 'luxon';
import DateField from '~/modules/common/components/DateField';

const useStyles = makeStyles(theme => ({
  dateRangeContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center'
  }
}));

const DateRangeField = ({ value, onChange, disabled }) => {
  const intl = useIntl();
  const classes = useStyles();

  const minDateForEndField = useMemo(() => {
    if (!value?.startDate) return undefined;

    return DateTime.fromObject(value.startDate, { zone: 'utc' });
  }, [value?.startDate]);

  const maxDateForStartField = useMemo(() => {
    if (!value?.endDate) return undefined;

    return DateTime.fromObject(value.endDate, { zone: 'utc' });
  }, [value?.endDate]);

  const handleStartDateChange = useCallback(
    newStartDate => {
      onChange({
        startDate: newStartDate,
        endDate: value?.endDate || null
      });
    },
    [onChange, value]
  );

  const handleEndDateChange = useCallback(
    newEndDate => {
      onChange({
        startDate: value?.startDate || null,
        endDate: newEndDate
      });
    },
    [onChange, value]
  );

  return (
    <Grid
      container
      spacing={1}
      wrap="nowrap"
      className={classes.dateRangeContainer}
    >
      <Grid item xs={6}>
        <DateField
          variant="filled"
          data-qe-id="MoveTimeDialogStartDate"
          label={intl.formatMessage({
            id: 'moveTimeDialog.criteria.startDate'
          })}
          ariaLabel={intl.formatMessage({
            id: 'moveTimeDialog.criteria.startDate'
          })}
          value={value?.startDate || null}
          onChange={handleStartDateChange}
          maxDate={maxDateForStartField}
          editable
          size="small"
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={6}>
        <DateField
          variant="filled"
          data-qe-id="MoveTimeDialogEndDate"
          label={intl.formatMessage({ id: 'moveTimeDialog.criteria.endDate' })}
          ariaLabel={intl.formatMessage({
            id: 'moveTimeDialog.criteria.endDate'
          })}
          value={value?.endDate || null}
          onChange={handleEndDateChange}
          minDate={minDateForEndField}
          editable
          size="small"
          disabled={disabled}
        />
      </Grid>
    </Grid>
  );
};

DateRangeField.propTypes = {
  value: PropTypes.shape({
    startDate: PropTypes.object,
    endDate: PropTypes.object
  }),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

export default DateRangeField;
